/**
 * The request page (spec 8.4) — Phase 2B, Task 15.
 *
 * A sibling file rather than more keys in `reviewRequests.composer.ts`, for
 * the reason `reviewRequests.list.ts` and `reviewRequests.confirm.ts` exist:
 * that file is at 361 lines against this repo's 400-line hard limit. The
 * whole `detail` object lives here and is spread back into the SAME
 * `reviewRequests.requests` namespace, so every key below is still
 * `reviewRequests.requests.detail.*` and no import path anywhere changed.
 *
 * The dispatch copy this screen shows is mostly NOT here: `dispatch.*` lives
 * in its own sibling, `reviewRequests.dispatch.ts`, and a second copy of
 * "Sending" is how the two would start disagreeing. Only the "Delivery"
 * section's own heading and intro sentence — which `dispatch.*` has no use
 * for — are added here (Task 14, Phase 2C).
 *
 * Same two constraints as its siblings: English only, and `{named}`
 * placeholders only — `cspSafeMessageCompiler` cannot parse vue-i18n's `|`
 * plural syntax, so count-dependent text is a manual `_one` / `_other` pair.
 */
export const reviewRequestsDetailMessages = {
  detail: {
    breadcrumb: "Review Request",
    title: "Review request",
    titleWithCode: "Review request {code}",

    // Sector denial and a missing id are indistinguishable server-side (both
    // 404 — `handlers/requests.rs::load_and_guard`), so this panel does not
    // try to tell them apart. Mirrors the roster and products screens.
    notFoundPanel:
      "This review request could not be found. It may be outside your sector, or it may not exist.",
    loadError: "Could not load this review request. Please try again.",
    // A draft reached by a pasted link or the browser's history -- the
    // dashboard's own draft rows open the composer directly.
    continueDraft: "Continue editing",

    // Said once, at the top: everything below is a record. The composer's
    // controls are absent from this page rather than disabled, and a SAM who
    // is not told why looks for them.
    //
    // TWO sentences, chosen on `submitted_at`. The dashboard opens a draft in
    // the composer, but a pasted link still reaches this page for a request
    // that has not been sent — and an unconditional "has been sent" there
    // would contradict the "Not sent yet" line four rows below it, the same
    // class of contradiction `list.status.dispatching` was reworded to
    // remove. Neither sentence promises the request can be changed HERE: the
    // unsent one points at the "Continue editing" button beside it. The
    // sent one names what is fixed (documents and scholars) rather than
    // "nothing", because a sent request that is dispatching or in review
    // still offers each scholar's "Revoke access" (`revoke.*` below), which
    // withdraws access and changes neither.
    readOnlyNoteSent:
      "This request has been sent, so it is shown here as a record. Its documents and scholars can no longer be changed.",
    readOnlyNoteUnsent:
      "This request has not been sent. It is shown here as a record — continue editing to change it.",

    factCode: "Reference",
    factSender: "Sent from",
    factSubmitted: "Sent",
    // Spec 3.2: the review folder is created with the SAM's own delegated
    // token, and where it landed is the audit fact this line exists for. Bound
    // as TEXT — a SharePoint path is user-supplied and there is no `v-html`
    // anywhere in this module.
    factFolder: "Review folder",
    // `sharepoint_parent_path` is null on a draft: the submit transaction is
    // what creates the folder. A sentence, not a blank cell, which would read
    // as data that failed to load.
    folderPending:
      "No review folder yet — one is created when the request is sent.",

    documentsTitle: "Documents under review",
    documentsEmpty: "No documents are attached to this request.",
    documentsCaption: "The documents this review covers",
    colDocument: "Document",
    colPages: "Pages",
    colBand: "Band",
    // `category_band` is per-document and nullable — no configured band covers
    // that page count. Named rather than left blank, for the same reason.
    bandNone: "No band",

    scholarsTitle: "Scholars",
    // The SAME cards the composer showed, now read-only (spec 8.4: "nothing
    // new to learn between composing and monitoring").
    scholarsNote:
      "The same cards the composer showed, carrying the arrangement and the price each review was recorded under.",
    scholarsEmpty: "No scholars are named on this request.",

    dispatchHeading: "Delivery",
    dispatchIntro:
      "Each scholar receives their own folder, their own copy of the documents, and a private link. Steps that need attention can be resent.",

    // Revoking one scholar's access (spec 6.3, `detail/ScholarRevoke.vue`).
    // Offered only while the request is dispatching or in review. The
    // confirmation says it cannot be undone because nothing re-grants it: the
    // stamp is permanent and the SharePoint permissions are deleted.
    revoke: {
      action: "Revoke access",
      // WCAG 2.5.3: the accessible name contains the visible "Revoke access".
      actionAria: "Revoke access for {name}",
      question:
        "Withdraw {name}'s access to these documents? Their links stop working and cannot be restored.",
      confirm: "Revoke access",
      revoking: "Revoking…",
      revokedOn: "Access revoked {date}",
      // A 404 is the sector guard's answer AND a scholar no longer on the
      // request; the two are indistinguishable server-side, deliberately.
      notFound:
        "This scholar could not be found on this request. Reload the page and try again.",
      failed: "Access could not be revoked. Please try again.",
      // The backend's 422 for a draft (`handlers/revoke.rs`). The UI only
      // offers the action once a request is sent, so this is a stale page.
      notSent:
        "This request has not been sent yet, so there is no access to revoke.",
    },
  },

  // `RequestsBackLink.vue`, above the record page and the composer. Named for
  // the destination, so the link says where it goes without an icon to decode.
  back: {
    label: "Requests Dashboard",
    navLabel: "Back to the Requests Dashboard",
    // The trail beside the button on the composer: no draft yet, then one.
    composerNew: "New review request",
    composerDraft: "Review request draft",
    // Beside the link on the composer once a draft exists: leaving is safe,
    // and this is where the work is picked up again.
    draftKept: "Your draft is saved. Reopen it from the dashboard any time.",
  },
};
